import axios from "axios";
import { useToast } from "vue-toastification";

// Create axios instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const toast = useToast();

    // Handle different error types
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const message = error.response.data?.error || "An error occurred";

      switch (status) {
        case 400:
          // Bad request - validation errors
          if (error.response.data?.details) {
            // Show first validation error
            const firstError = error.response.data.details[0]?.msg || message;
            toast.error(firstError);
          } else {
            toast.error(message);
          }
          break;
        case 401:
          // Unauthorized - token invalid or missing
          toast.error("Session expired. Please login again.");
          // Clear token and redirect to login
          localStorage.removeItem("token");
          if (window.location.pathname !== "/login") {
            window.location.href = "/login";
          }
          break;
        case 403:
          // Forbidden - no permission
          toast.error("You do not have permission to perform this action.");
          break;
        case 404:
          // Not found
          toast.error("Resource not found.");
          break;
        case 429:
          // Rate limited
          toast.error("Too many requests. Please try again later.");
          break;
        case 500:
          // Server error
          toast.error("Server error. Please try again later.");
          break;
        default:
          toast.error(message);
      }
    } else if (error.request) {
      // Request made but no response received
      toast.error("Network error. Please check your connection.");
    } else {
      // Something else happened
      toast.error("An unexpected error occurred.");
    }

    return Promise.reject(error);
  }
);

// API methods for different endpoints
export const authAPI = {
  login: (credentials) => api.post("/auth/login", credentials),
  register: (userData) => api.post("/auth/register", userData),
  socialLogin: (providerData) => api.post("/auth/social-login", providerData),
  getProfile: () => api.get("/auth/me"),
  updateProfile: (profileData) => api.put("/auth/profile", profileData),
};

export const mediaAPI = {
  upload: (formData) =>
    api.post("/media/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  getAll: (params) => api.get("/media", { params }),
  update: (mediaId, data) => api.put(`/media/${mediaId}`, data),
  delete: (mediaId) => api.delete(`/media/${mediaId}`),
};

export const pricingAPI = {
  get: () => api.get("/pricing"),
  updateBase: (data) => api.put("/pricing/base", data),
  updateModifiers: (data) => api.put("/pricing/modifiers", data),
  updateDynamic: (data) => api.put("/pricing/dynamic", data),
  updateAppRates: (rates) => api.put("/pricing/app-rates", { rates }),
  calculate: (data) => api.post("/pricing/calculate", data),
  reset: () => api.post("/pricing/reset"),
  getAnalytics: () => api.get("/pricing/analytics"),
};

export const approvalsAPI = {
  get: () => api.get("/approvals"),
  updateCategory: (data) => api.put("/approvals/categories", data),
  updateSubcategory: (data) => api.put("/approvals/subcategories", data),
  addNaturalLanguageRule: (data) =>
    api.post("/approvals/natural-language", data),
  deleteNaturalLanguageRule: (ruleId) =>
    api.delete(`/approvals/natural-language/${ruleId}`),
  updateAppPermissions: (data) => api.put("/approvals/apps", data),
  updateGeoRestrictions: (data) => api.put("/approvals/geo", data),
  testGuardrailsPrompt: (data) => api.post("/approvals/guardrails/test", data),
};

export const analyticsAPI = {
  getOverview: (params) => api.get("/analytics/overview", { params }),
  getRevenue: (params) => api.get("/analytics/revenue", { params }),
  getUsage: (params) => api.get("/analytics/usage", { params }),
  getGenerationStats: (params) => api.get("/analytics/generations", { params }),
};

export const generationsAPI = {
  getAll: (params) => api.get("/generations", { params }),
  getById: (id) => api.get(`/generations/${id}`),
  approve: (id, data) => api.post(`/generations/${id}/approve`, data),
  reject: (id, data) => api.post(`/generations/${id}/reject`, data),
  getPending: () => api.get("/generations/pending"),
};

export const payoutsAPI = {
  getSettings: () => api.get("/payouts"),
  setupStripe: () => api.post("/payouts/stripe"),
  setupPayPal: () => api.post("/payouts/paypal"),
  getHistory: (params) => api.get("/payouts/history", { params }),
  requestPayout: (data) => api.post("/payouts/request", data),
};

// Utility functions
export const downloadFile = (url, filename) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const formatCurrency = (amount, currency = "USD") => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
  }).format(amount);
};

export const formatDate = (date, options = {}) => {
  const defaultOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  };

  return new Intl.DateTimeFormat("en-US", {
    ...defaultOptions,
    ...options,
  }).format(new Date(date));
};

export const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

export default api;
